import Button from 'ext-ui/components/Button/Button'
import { white100 } from 'ext-ui/style/colors'
import React, { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavOption } from 'src/components/Subnav/Subnav'
import SvgExpand from 'src/components/Svgs/SvgExpand'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import ExpenseRequirementsAppRoute from 'src/navigation/appRoutes/organization/expenseRequirements.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import { expenseRequirementsRouteProps } from 'src/navigation/routeProps'
import {
  expenseRequirementsAdditionalFieldsUrl,
  expenseRequirementsReceiptRulesUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const ExpenseRequirements: React.FC<React.PropsWithChildren<unknown>> = () => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const translate = useTranslate()
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const extendLocation = useExtendLocation()
  const currentUser = useSelector(selectUserListModelFromState)
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const appName = useSelector(selectAppName)
  const { enable_custom_fields_expense_fields: customFieldsExpenseFieldsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const [addingNewField, setAddingNewField] = useState<boolean>(false)

  const {
    EDIT_ADDITIONAL_FIELDS: hasEditAdditionalFieldsPermissions,
    VIEW_ADDITIONAL_FIELDS: hasViewAdditionalFieldsPermissions,
  } =
    useQueryUserFeatures([
      UserFeature.EDIT_ADDITIONAL_FIELDS,
      UserFeature.VIEW_ADDITIONAL_FIELDS,
    ]).data || {}
  const canAddField =
    hasEditAdditionalFieldsPermissions && hasViewAdditionalFieldsPermissions

  const expenseRequirementsSubnavOptions = useMemo(
    (): SubnavOption[] => [
      {
        label: translate('Receipt Rules'),
        to: expenseRequirementsReceiptRulesUrl(
          organizationId !== undefined ? organizationId : '',
        ),
        isActive:
          extendLocation.location ===
          AppLocation.OrganizationSettingsExpenseRequirementsReceiptRules,
        ariaLabelSource: translate('Receipt Rules'),
        'data-testid': 'subnavExpenseRequirementsReceiptRules',
      },
      ...(!customFieldsExpenseFieldsEnabled
        ? [
            {
              label: translate('Additional Fields'),
              to: expenseRequirementsAdditionalFieldsUrl(
                organizationId !== undefined ? organizationId : '',
              ),
              isActive:
                extendLocation.location ===
                AppLocation.OrganizationSettingsExpenseRequirementsAdditionalFields,
              ariaLabelSource: translate('Additional Fields'),
              'data-testid': 'subnavExpenseRequirementsAdditionalFields',
            },
          ]
        : []),
    ],
    [customFieldsExpenseFieldsEnabled, extendLocation, organizationId, translate],
  )

  if (!loadedRemoteConfig) {
    return <LoadingBackgroundPage />
  }

  if (!currentUser?.hasOrgBookkeeperPermissions) {
    return <NotFoundBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Expense Requirements | ${appName}`}</title>
        <meta
          name="description"
          content={`Expense Requirements page of ${appName}`}
        />
      </Helmet>
      <LayoutPage
        subnavOptions={expenseRequirementsSubnavOptions}
        title={<span>{translate('Expense Requirements')}</span>}
        actions={
          <>
            {!customFieldsExpenseFieldsEnabled &&
              extendLocation.location ===
                AppLocation.OrganizationSettingsExpenseRequirementsAdditionalFields &&
              canAddField && (
                <Button
                  data-testid="new-category-btn"
                  variant={'primary'}
                  dropShadow={false}
                  iconRight={
                    <SvgExpand
                      width={16}
                      height={16}
                      viewBox={'0 0 24 24'}
                      fill={white100}
                    />
                  }
                  onClick={() => {
                    setAddingNewField(true)
                  }}
                  disabled={addingNewField}
                >
                  {translate('New Field')}
                </Button>
              )}
          </>
        }
      >
        <Switch location={calculatedBackgroundLocation}>
          {ExpenseRequirementsAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component &&
                React.cloneElement(route.component, {
                  addingNewField,
                  setAddingNewField,
                })}
            </Route>
          ))}
          <Route {...expenseRequirementsRouteProps} exact>
            <Rdrct
              to={expenseRequirementsReceiptRulesUrl(
                organizationId !== undefined ? organizationId : '',
              )}
            />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      </LayoutPage>
    </>
  )
}

export default ExpenseRequirements
